/** First-run screen: pick a source, show progress, hand off when the vault is populated. */

import {
  CdImportError,
  ImportError,
  directoryPickerAvailable,
  filesFromCd,
  filesFromDevEndpoint,
  filesFromInput,
  filesFromZip,
  ingest,
  isCueFile,
  isImageFile,
  looksLikeCd,
  pickDirectory,
  type ImportProgress,
  type ImportResult,
  type SourceFile,
} from '../import';
import { recordPersistGrant } from '../durability';
import { readVaultManifest, restoreVaultBackup } from '../vault-backup';
import { $, bytes, note, toast } from './dom';

/** A source loader may return bare files, or files plus things worth reporting. */
type Loaded = SourceFile[] | { files: SourceFile[]; notes?: string[] };

export function isDevGameData(): boolean {
  return import.meta.env.DEV && new URLSearchParams(location.search).get('dev') === 'gamedata';
}

/** `/simulator` — headless presentation over the same staged arena session. */
export function isDevSimulator(): boolean {
  const path = location.pathname.replace(/\/+$/, '');
  return import.meta.env.DEV && (path === '/simulator' || path === '/combat-sim');
}

/** `/arena` (or legacy `?arena=1`) — CE-native single-encounter arena. */
export function isDevArena(): boolean {
  const path = location.pathname.replace(/\/+$/, '');
  return import.meta.env.DEV
    && (path === '/arena' || isDevSimulator() || new URLSearchParams(location.search).has('arena'));
}

/** `?dev=direct` — boot straight off the dev endpoint, no vault. See dev/gamedata.ts. */
export function isDevDirectMount(): boolean {
  return import.meta.env.DEV && new URLSearchParams(location.search).get('dev') === 'direct';
}

export function mountInstaller(onDone: (r: ImportResult) => void, onRestored: () => void = () => {}): void {
  const progress = $('#import-progress');
  const bar = $<HTMLElement>('#import-progress .bar > i');
  const stage = $('#import-progress .stage');
  const fileEl = $('#import-progress .file');
  const pct = $('#import-progress .pct');
  const messages = $('#import-messages');
  let busy = false;

  const onProgress = (p: ImportProgress) => {
    progress.classList.add('on');
    const ratio = p.bytesTotal ? p.bytesDone / p.bytesTotal : 0;
    bar.style.width = `${(ratio * 100).toFixed(1)}%`;
    stage.textContent = `${p.stage} — ${p.filesDone}/${p.filesTotal} files, ${bytes(p.bytesDone)} of ${bytes(p.bytesTotal)}`;
    fileEl.textContent = p.file ?? '';
    pct.textContent = `${Math.round(ratio * 100)}%`;
  };

  const run = async (source: Parameters<typeof ingest>[1], load: () => Promise<Loaded>) => {
    if (busy) return;
    busy = true;
    messages.replaceChildren();
    progress.classList.add('on');
    stage.textContent = 'Reading file list…';
    bar.style.width = '0%';
    try {
      const loaded = await load();
      const files = Array.isArray(loaded) ? loaded : loaded.files;
      // Disc geometry and name-table findings, said before the copy starts so
      // they are on screen while the half-gigabyte read runs.
      if (!Array.isArray(loaded)) for (const n of loaded.notes ?? []) note(messages, n, 'ok');
      const result = await ingest(files, source, onProgress);
      // Persistent storage is asked for here and nowhere else: this is the
      // moment of clearest intent, the player has just handed us their own copy
      // of the game. Recorded either way, never retried, never nagged about.
      const { granted: persisted } = await recordPersistGrant();
      if (!persisted) {
        note(
          messages,
          'This browser would not grant persistent storage, so it may evict the imported data under disk pressure. Your saves can always be exported as .zip.',
          'warn'
        );
      }
      for (const w of result.warnings) note(messages, w, 'warn');
      if (result.quarantined.length) {
        const shown = result.quarantined.slice(0, 6).join(', ');
        const more = result.quarantined.length > 6 ? `, +${result.quarantined.length - 6} more` : '';
        note(
          messages,
          `${result.quarantined.length} mod-loader files skipped (${shown}${more}). ` +
            'These configure sfall and the Hi-Res patch, which this engine reimplements natively — ' +
            'keeping them would let settings written for a different renderer override ours.',
          'ok'
        );
      }
      note(
        messages,
        `Imported ${result.meta.fileCount} files (${bytes(result.meta.totalBytes)}).`,
        'ok'
      );
      onDone(result);
    } catch (e) {
      progress.classList.remove('on');
      if (e && typeof e === 'object' && 'name' in e && (e as DOMException).name === 'AbortError') {
        // user cancelled the picker; not an error
      } else if (e instanceof CdImportError && e.missingFile) {
        // A .cue on its own is useless — it is a table of contents for a .bin
        // the browser has no way to reach from the cue alone. Ask, rather than
        // making the user work out that they need to drop both at once.
        note(messages, e.message, 'warn');
        askForBin(e.missingFile);
      } else {
        const msg = e instanceof ImportError ? e.message : e instanceof Error ? e.message : String(e);
        note(messages, msg, 'bad');
        console.error('[import]', e);
      }
    } finally {
      busy = false;
    }
  };

  /**
   * A save backup dropped on the game-data importer.
   *
   * Recognised by the manifest inside, never by the extension, and handled
   * here rather than rejected: someone recovering an evicted install has one
   * folder of files and no reason to know which card wants which. Returns true
   * when the file was ours, so the caller can stop looking.
   */
  const takeVaultBackup = async (file: File): Promise<boolean> => {
    if (!(await readVaultManifest(file))) return false;
    messages.replaceChildren();
    try {
      const summary = await restoreVaultBackup(file);
      note(
        messages,
        `Restored ${summary.restored} save${summary.restored === 1 ? '' : 's'} to your archive. `
          + 'They are waiting in the save archive; nothing in a slot was changed.',
        'ok'
      );
      onRestored();
    } catch (e) {
      note(messages, `That backup could not be restored: ${e instanceof Error ? e.message : String(e)}`, 'bad');
    }
    return true;
  };

  // --- folder ---------------------------------------------------------------
  const inputDir = $<HTMLInputElement>('#input-dir');
  $('#btn-pick-dir').addEventListener('click', () => {
    if (directoryPickerAvailable()) void run('directory', pickDirectory);
    else inputDir.click(); // Safari / Firefox: webkitdirectory input
  });
  inputDir.addEventListener('change', () => {
    if (inputDir.files?.length) void run('directory', async () => filesFromInput(inputDir.files!));
  });

  // --- zip ------------------------------------------------------------------
  const inputZip = $<HTMLInputElement>('#input-zip');
  $('#btn-pick-zip').addEventListener('click', () => inputZip.click());
  inputZip.addEventListener('change', () => {
    const f = inputZip.files?.[0];
    if (f) {
      void takeVaultBackup(f).then((taken) => {
        if (!taken) void run('zip', () => filesFromZip(f));
      });
    }
  });

  const dz = $('#dropzone');
  const hot = (on: boolean) => dz.classList.toggle('hot', on);
  for (const type of ['dragenter', 'dragover'] as const) {
    dz.addEventListener(type, (e) => {
      e.preventDefault();
      hot(true);
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) dz.addEventListener(type, () => hot(false));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    hot(false);
    const dt = (e as DragEvent).dataTransfer;
    if (!dt) return;
    const dropped = Array.from(dt.files);
    const file = dropped.find((f) => /\.zip$/i.test(f.name));
    // A save backup takes precedence over every game-data reading of the same
    // drop: it is small, it is unambiguous once the manifest is read, and a
    // player restoring one has usually just lost everything.
    const lone = dropped.length === 1 ? dropped[0] : dropped.find((f) => /\.rrdata$/i.test(f.name));
    void (lone ? takeVaultBackup(lone) : Promise.resolve(false)).then((taken) => {
      if (taken) return;
      // Aiming is not a skill we should demand: a disc image dropped on the zip
      // card is obviously meant for the disc reader.
      if (looksLikeCd(dropped)) runCd(dropped);
      else if (file) void run('zip', () => filesFromZip(file));
      // `dropped`, not `dt.files`: the DataTransfer is only valid for the
      // duration of the handler, and reading the manifest made this async.
      else if (dropped.length) void run('directory', async () => filesFromInput(dropped));
      else toast('Drop a .zip of your Fallout 2 install.', 'warn');
    });
  });
  // Dropping anywhere else must not navigate the page away.
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => e.preventDefault());

  // --- CD image -------------------------------------------------------------
  //
  // A cue and its bin are two files, and the browser gives us no way to reach a
  // sibling on disk from one of them. Dropping both together is the happy path;
  // when only the cue arrives we remember it and ask for the image by name.
  const inputCd = $<HTMLInputElement>('#input-cd');
  const inputCdBin = $<HTMLInputElement>('#input-cd-bin');
  let pendingCue: File | null = null;

  const runCd = (files: File[]) => {
    pendingCue = files.find(isCueFile) ?? null;
    void run('cd', () => filesFromCd(files));
  };

  /**
   * Offers a button rather than opening the picker itself.
   *
   * `inputCdBin.click()` looks tidier and does not work: by the time the cue has
   * been read and parsed the user activation from the original click has
   * expired, and the browser blocks the file dialog. A button the user presses
   * carries its own activation.
   */
  function askForBin(name: string): void {
    if (!pendingCue) return;
    const row = document.createElement('div');
    row.className = 'row';
    const btn = document.createElement('button');
    btn.className = 'primary';
    btn.textContent = `Choose ${name}…`;
    btn.addEventListener('click', () => inputCdBin.click());
    row.append(btn);
    messages.append(row);
  }

  $('#btn-pick-cd').addEventListener('click', () => inputCd.click());
  inputCd.addEventListener('change', () => {
    if (inputCd.files?.length) runCd(Array.from(inputCd.files));
  });
  inputCdBin.addEventListener('change', () => {
    const bin = inputCdBin.files?.[0];
    if (bin && pendingCue) {
      const cue = pendingCue;
      void run('cd', () => filesFromCd([cue, bin]));
    }
    inputCdBin.value = '';
  });

  const cdz = $('#cd-dropzone');
  const cdHot = (on: boolean) => cdz.classList.toggle('hot', on);
  for (const type of ['dragenter', 'dragover'] as const) {
    cdz.addEventListener(type, (e) => {
      e.preventDefault();
      cdHot(true);
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) cdz.addEventListener(type, () => cdHot(false));
  cdz.addEventListener('drop', (e) => {
    e.preventDefault();
    cdHot(false);
    const dropped = Array.from((e as DragEvent).dataTransfer?.files ?? []);
    if (dropped.some((f) => isCueFile(f) || isImageFile(f))) runCd(dropped);
    else toast('Drop a .cue and its .bin, or a lone .bin/.iso.', 'warn');
  });

  // --- dev endpoint ---------------------------------------------------------
  if (import.meta.env.DEV) {
    const row = document.getElementById('dev-import-row');
    if (row) {
      row.hidden = false;
      $('#btn-dev-import').addEventListener('click', () => void run('dev-endpoint', filesFromDevEndpoint));
    }
  }
}
